/**
 * UpdateControlsPanel — "Auto-update" suggestions for the packaging wizard.
 *
 * Calls GET /api/packages/{id}/update-controls and shows what the backend
 * found that would stop the packaged app updating itself:
 *
 *  - MSI: update-related properties (applied as property overrides), plus
 *    hard-coded registry values and updater features (queued as MST table
 *    edits in MstAdvancedOverrides) and update services (advice only).
 *  - EXE: documented install switches for the matched app (appended to the
 *    install switches).
 *
 * Everything here is a suggestion. A catalogued entry is vendor-documented;
 * a "name match" is a guess from the property or value name. The packager
 * decides, and it still needs proving on a test device.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Check, Loader2, RefreshCw, ShieldOff } from "lucide-react";

const SOURCE_BADGE = {
  catalogue: { label: "documented", cls: "border-emerald-500/40 text-emerald-300" },
  pattern: { label: "name match", cls: "border-amber-500/40 text-amber-300" },
};

function SourceBadge({ source, vendor }) {
  const b = SOURCE_BADGE[source] || SOURCE_BADGE.pattern;
  return (
    <span
      className={`text-[10px] px-1.5 py-0.5 rounded border ${b.cls}`}
      title={vendor ? `Documented for ${vendor}` : "Inferred from the name; check the vendor's documentation"}
    >
      {b.label}
    </span>
  );
}

function ActionButton({ done, onClick, disabled, children, testId }) {
  if (done) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-emerald-300">
        <Check className="h-3 w-3" /> Applied
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="px-2 py-0.5 text-[11px] rounded bg-cyan-600/80 hover:bg-cyan-600 text-white disabled:opacity-50 whitespace-nowrap"
      data-testid={testId}
    >
      {children}
    </button>
  );
}

export default function UpdateControlsPanel({
  apiBase,
  packageId,
  authHeaders,
  mode, // "msi" | "exe"
  disabled = false,
  // MSI
  propertyOverrides = {},
  onApplyProperty,
  useMst = false,
  onEnableMst,
  onQueueTableEdit,
  // EXE
  installSwitches = "",
  onAddSwitch,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [queued, setQueued] = useState({}); // "registry:<id>" / "feature:<id>" → true

  const load = useCallback(async () => {
    if (!packageId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${apiBase}/packages/${packageId}/update-controls`, {
        headers: await authHeaders(),
      });
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Could not check for update controls");
    } finally {
      setLoading(false);
    }
  }, [apiBase, packageId, authHeaders]);

  useEffect(() => {
    setQueued({});
    load();
  }, [load]);

  const msi = data?.msi;
  const exeSwitches = data?.exe_switches || [];
  const total = mode === "msi" ? msi?.count || 0 : exeSwitches.length;

  // Same rule as the backend's switch_already_present: compare switch names,
  // so "/AutoUpdateCheck=disabled" counts as present if any value is set.
  const switchPresent = (sw) => {
    const key = sw.split(/[=:]/)[0].toLowerCase();
    return (installSwitches || "").split(/\s+/).some((t) => t.split(/[=:]/)[0].toLowerCase() === key);
  };

  const queueTable = (key, table, edit) => {
    if (!useMst) {
      onEnableMst?.();
      return;
    }
    if (onQueueTableEdit?.(table, edit)) {
      setQueued((q) => ({ ...q, [key]: true }));
    }
  };

  return (
    <div className="border border-slate-700 rounded-lg overflow-hidden" data-testid="update-controls-panel">
      <div className="flex items-center justify-between p-3 bg-gradient-to-r from-slate-800 to-slate-850">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${total > 0 ? "bg-violet-900/50" : "bg-slate-700"}`}>
            <ShieldOff className={`h-4 w-4 ${total > 0 ? "text-violet-300" : "text-slate-400"}`} />
          </div>
          <div>
            <h4 className="font-medium text-sm text-slate-200">Auto-update controls</h4>
            <p className="text-xs text-slate-400">
              {loading
                ? "Checking the installer…"
                : total > 0
                ? `${total} suggestion${total === 1 ? "" : "s"} to stop the app updating itself outside ALICE.`
                : data?.note || "Nothing found that controls the app's own updates."}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="text-slate-400 hover:text-slate-200 disabled:opacity-50"
          title="Check again"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </button>
      </div>

      {error && <div className="px-3 py-2 text-[11px] text-red-400 border-t border-slate-700">{error}</div>}

      {total > 0 && (
        <div className="p-3 border-t border-slate-700 bg-slate-900/60 space-y-3 text-xs">
          {/* EXE: catalogued install switches */}
          {mode === "exe" &&
            exeSwitches.map((s) => (
              <div key={s.switch} className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-slate-200">{s.switch}</span>
                    <SourceBadge source={s.source} vendor={s.vendor} />
                    <span className="text-[10px] text-slate-500">{s.vendor}</span>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-0.5">{s.note}</p>
                </div>
                <ActionButton
                  done={switchPresent(s.switch)}
                  disabled={disabled}
                  onClick={() => onAddSwitch?.(s.switch)}
                  testId={`update-controls-add-switch-${s.switch}`}
                >
                  Add to install switches
                </ActionButton>
              </div>
            ))}

          {/* MSI: properties */}
          {mode === "msi" && msi?.properties?.length > 0 && (
            <div className="space-y-2">
              <h5 className="text-[11px] font-semibold text-slate-300 uppercase tracking-wide">Properties</h5>
              {msi.properties.map((p) => {
                const applied =
                  p.suggested_value != null &&
                  (propertyOverrides[p.name] === p.suggested_value ||
                    (p.already_set && propertyOverrides[p.name] === undefined));
                return (
                  <div key={p.name} className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-slate-200">{p.name}</span>
                        <SourceBadge source={p.source} vendor={p.vendor} />
                        {p.requires_mst && (
                          <span
                            className="text-[10px] px-1.5 py-0.5 rounded border border-cyan-500/40 text-cyan-300"
                            title="Private property (lower-case letters): msiexec ignores it on the command line, so it only takes effect through the MST."
                          >
                            MST only
                          </span>
                        )}
                        <span className="text-[10px] text-slate-500 font-mono">
                          default: {p.declared_in_property_table ? p.current_value || '""' : "not set"}
                          {p.suggested_value != null && <> → suggested: {p.suggested_value}</>}
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-400 mt-0.5">
                        {p.vendor && <span className="text-slate-500">{p.vendor}. </span>}
                        {p.note}
                        {p.registry_refs?.length > 0 && (
                          <span className="text-slate-500"> Writes {p.registry_refs.join(", ")}.</span>
                        )}
                      </p>
                    </div>
                    {p.suggested_value != null && (
                      <ActionButton
                        done={applied}
                        disabled={disabled}
                        onClick={() => {
                          onApplyProperty?.(p.name, p.suggested_value);
                          if (p.requires_mst && !useMst) onEnableMst?.();
                        }}
                        testId={`update-controls-apply-${p.name}`}
                      >
                        Set {p.suggested_value}
                      </ActionButton>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* MSI: hard-coded registry values */}
          {mode === "msi" && msi?.registry?.length > 0 && (
            <div className="space-y-2">
              <h5 className="text-[11px] font-semibold text-slate-300 uppercase tracking-wide">Registry values</h5>
              {msi.registry.map((r) => {
                const key = `registry:${r.registry_id}`;
                return (
                  <div key={key} className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-slate-200 break-all">{r.key}\{r.name}</span>
                        <SourceBadge source={r.source} />
                        <span className="text-[10px] text-slate-500 font-mono">
                          {r.current_value}
                          {r.suggested_value != null && <> → {r.suggested_value}</>}
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-400 mt-0.5">{r.note}</p>
                    </div>
                    {r.suggested_value != null && !r.already_set && (
                      <ActionButton
                        done={queued[key]}
                        disabled={disabled}
                        onClick={() =>
                          queueTable(key, "Registry", {
                            op: "update",
                            row: { Registry: r.registry_id, Value: r.suggested_value },
                            keyRow: { Registry: r.registry_id },
                          })
                        }
                        testId={`update-controls-registry-${r.registry_id}`}
                      >
                        {useMst ? "Queue in MST" : "Turn on MST"}
                      </ActionButton>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* MSI: updater features */}
          {mode === "msi" && msi?.features?.length > 0 && (
            <div className="space-y-2">
              <h5 className="text-[11px] font-semibold text-slate-300 uppercase tracking-wide">Features</h5>
              {msi.features.map((f) => {
                const key = `feature:${f.feature}`;
                return (
                  <div key={key} className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-slate-200">{f.feature}</span>
                        {f.title && <span className="text-slate-400">{f.title}</span>}
                        <SourceBadge source={f.source} />
                        <span className="text-[10px] text-slate-500 font-mono">
                          Level {f.current_level} → {f.suggested_level}
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-400 mt-0.5">{f.note}</p>
                    </div>
                    {!f.already_set && (
                      <ActionButton
                        done={queued[key]}
                        disabled={disabled}
                        onClick={() =>
                          queueTable(key, "Feature", {
                            op: "update",
                            row: { Feature: f.feature, Level: f.suggested_level },
                            keyRow: { Feature: f.feature },
                          })
                        }
                        testId={`update-controls-feature-${f.feature}`}
                      >
                        {useMst ? "Queue in MST" : "Turn on MST"}
                      </ActionButton>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* MSI: update services (advice only) */}
          {mode === "msi" && msi?.services?.length > 0 && (
            <div className="space-y-2">
              <h5 className="text-[11px] font-semibold text-slate-300 uppercase tracking-wide">Services</h5>
              {msi.services.map((s) => (
                <div key={s.service} className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-slate-200">{s.service}</span>
                    {s.display_name && <span className="text-slate-400">{s.display_name}</span>}
                    <SourceBadge source={s.source} />
                  </div>
                  <p className="text-[11px] text-slate-400 mt-0.5">{s.note}</p>
                </div>
              ))}
            </div>
          )}

          {mode === "msi" && (msi?.registry?.length > 0 || msi?.features?.length > 0) && (
            <p className="text-[11px] text-slate-500 italic">
              Registry and feature changes are queued in Advanced table overrides. Press Save there to keep them.
            </p>
          )}
          <p className="text-[11px] text-slate-500 italic">
            Suggestions only. Confirm the app no longer updates itself on a test device before going live.
          </p>
        </div>
      )}
    </div>
  );
}
